"use client";

import { Select as SelectPrimitive } from "@base-ui/react/select";
import {
  CaretSortIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@radix-ui/react-icons";
import * as React from "react";

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useOwnTheme } from "@/contexts/theme-context";
import { useHistoryManager } from "@/hooks/use-history-manager";
import { useMobile } from "@/hooks/use-mobile";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

type SelectValueType<
  Value,
  Multiple extends boolean | undefined,
> = Multiple extends true ? Value[] : Value;

type PreventableSelectItemClickEvent = React.MouseEvent<HTMLDivElement> & {
  preventBaseUIHandler: () => void;
};

type SelectProps<
  Value = string,
  Multiple extends boolean | undefined = false,
> = Omit<
  React.ComponentProps<typeof SelectPrimitive.Root<Value, Multiple>>,
  "defaultValue" | "onValueChange" | "value"
> & {
  defaultValue?: SelectValueType<Value, Multiple> | null;
  drawerTitle?: string;
  historyName?: string;
  onValueChange?: (
    value: SelectValueType<Value, Multiple>,
    eventDetails: SelectPrimitive.Root.ChangeEventDetails,
  ) => void;
  value?: SelectValueType<Value, Multiple> | null;
};

type SelectSurfaceContextValue = {
  commitDrawerItemValue: (itemValue: unknown, event: MouseEvent) => void;
  isDrawer: boolean;
  open: boolean;
  sectionRadius: string;
  setOpen: (
    open: boolean,
    eventDetails?: SelectPrimitive.Root.ChangeEventDetails,
  ) => void;
  title: string;
};

const SelectSurfaceContext =
  React.createContext<SelectSurfaceContextValue | null>(null);

const useSelectSurfaceContext = () => {
  const context = React.useContext(SelectSurfaceContext);

  if (!context) {
    throw new Error("useSelectSurfaceContext must be used within Select");
  }

  return context;
};

const useOptionalSelectSurfaceContext = () =>
  React.useContext(SelectSurfaceContext);

const Select = <Value, Multiple extends boolean | undefined = false>({
  onValueChange,
  items,
  children,
  ...props
}: SelectProps<Value, Multiple>) => (
  <SelectRootWithDerivedItems<Value, Multiple>
    items={items}
    onValueChange={onValueChange}
    {...props}
  >
    {children}
  </SelectRootWithDerivedItems>
);

type DerivedSelectItem = {
  label: string;
  value: unknown;
};

type SelectItemLikeProps = {
  children?: React.ReactNode;
  value?: unknown;
};

function isSelectItemNode(
  child: React.ReactElement<SelectItemLikeProps>,
): boolean {
  if (child.type === SelectItem) {
    return true;
  }

  if (typeof child.type === "string") {
    return false;
  }

  const displayName =
    "displayName" in child.type ? child.type.displayName : undefined;

  return displayName === "SelectItem";
}

function normalizeSelectLabel(value: string) {
  return value.replaceAll("\u00A0", " ").replace(/\s+/g, " ").trim();
}

function extractTextContent(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") {
    return "";
  }

  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(extractTextContent).join(" ");
  }

  if (!React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return "";
  }

  return extractTextContent(node.props.children);
}

function collectSelectItems(node: React.ReactNode): DerivedSelectItem[] {
  const items: DerivedSelectItem[] = [];

  React.Children.forEach(node, (child) => {
    if (!React.isValidElement<SelectItemLikeProps>(child)) {
      return;
    }

    const props = child.props;

    if (isSelectItemNode(child)) {
      const label = normalizeSelectLabel(extractTextContent(props.children));

      items.push({
        label,
        value: props.value ?? null,
      });
      return;
    }

    if (props.children !== undefined) {
      items.push(...collectSelectItems(props.children));
    }
  });

  return items;
}

function createSelectItemPressEventDetails(
  event: MouseEvent,
): SelectPrimitive.Root.ChangeEventDetails {
  let canceled = false;
  let allowPropagation = false;

  return {
    reason: "item-press",
    event,
    cancel() {
      canceled = true;
    },
    allowPropagation() {
      allowPropagation = true;
    },
    get isCanceled() {
      return canceled;
    },
    get isPropagationAllowed() {
      return allowPropagation;
    },
    trigger: undefined,
  } as SelectPrimitive.Root.ChangeEventDetails;
}

const SelectRootWithDerivedItems = <
  Value,
  Multiple extends boolean | undefined = false,
>({
  defaultValue = null,
  defaultOpen = false,
  drawerTitle,
  historyName,
  id,
  highlightItemOnHover,
  modal,
  multiple,
  name,
  onOpenChange,
  onValueChange,
  open: openProp,
  value: valueProp,
  items,
  children,
  ...props
}: SelectProps<Value, Multiple>) => {
  const { isMobile } = useMobile();
  const { theme } = useOwnTheme();
  const reactInstanceId = React.useId();
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const [internalValue, setInternalValue] = React.useState<SelectValueType<
    Value,
    Multiple
  > | null>(defaultValue);
  const isOpenControlled = openProp !== undefined;
  const isValueControlled = valueProp !== undefined;
  const open = isOpenControlled ? openProp : internalOpen;
  const value = isValueControlled ? valueProp : internalValue;
  const openRef = React.useRef(open);
  const isDrawer = isMobile;
  const title = drawerTitle ?? historyName ?? name ?? "Select option";
  const sectionRadius = theme?.startsWith("paper") ? "4px" : "var(--radius)";
  openRef.current = open;

  const derivedItems = React.useMemo(() => {
    if (items != null) {
      return items;
    }

    const collectedItems = collectSelectItems(children);
    return collectedItems.length > 0 ? collectedItems : undefined;
  }, [children, items]);

  const applyOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isOpenControlled) {
        setInternalOpen(nextOpen);
      }
    },
    [isOpenControlled],
  );

  React.useEffect(() => {
    if (!isValueControlled) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, isValueControlled]);

  const history = useHistoryManager({
    enabled: isDrawer,
    kind: "select-drawer",
    name: historyName ?? name ?? id ?? reactInstanceId,
    onOpenChange: applyOpen,
    open,
  });
  const historyOnOpenChange = history.onOpenChange;

  const setOpen = React.useCallback(
    (
      nextOpen: boolean,
      eventDetails?: SelectPrimitive.Root.ChangeEventDetails,
    ) => {
      if (nextOpen === openRef.current) {
        return;
      }

      openRef.current = nextOpen;

      if (eventDetails) {
        onOpenChange?.(nextOpen, eventDetails);
      }

      if (isDrawer) {
        historyOnOpenChange(nextOpen);
        return;
      }

      applyOpen(nextOpen);
    },
    [applyOpen, historyOnOpenChange, isDrawer, onOpenChange],
  );

  const commitDrawerItemValue = React.useCallback(
    (itemValue: unknown, event: MouseEvent) => {
      const eventDetails = createSelectItemPressEventDetails(event);

      if (multiple) {
        const currentValue = Array.isArray(value) ? value : [];
        const isItemEqualToValue = props.isItemEqualToValue ?? Object.is;
        const nextValue = currentValue.some((currentItem) =>
          isItemEqualToValue(currentItem as Value, itemValue as Value),
        )
          ? currentValue.filter(
              (currentItem) =>
                !isItemEqualToValue(currentItem as Value, itemValue as Value),
            )
          : [...currentValue, itemValue];

        if (!isValueControlled) {
          setInternalValue(nextValue as SelectValueType<Value, Multiple>);
        }

        onValueChange?.(
          nextValue as SelectValueType<Value, Multiple>,
          eventDetails,
        );
        return;
      }

      if (!isValueControlled) {
        setInternalValue(itemValue as SelectValueType<Value, Multiple>);
      }

      if (itemValue !== null) {
        onValueChange?.(
          itemValue as SelectValueType<Value, Multiple>,
          eventDetails,
        );
      }

      setOpen(false, eventDetails);
    },
    [
      isValueControlled,
      multiple,
      onValueChange,
      props.isItemEqualToValue,
      setOpen,
      value,
    ],
  );

  const surfaceContext = React.useMemo<SelectSurfaceContextValue>(
    () => ({
      commitDrawerItemValue,
      isDrawer,
      open,
      sectionRadius,
      setOpen,
      title,
    }),
    [commitDrawerItemValue, isDrawer, open, sectionRadius, setOpen, title],
  );

  return (
    <SelectSurfaceContext.Provider value={surfaceContext}>
      <SelectPrimitive.Root<Value, Multiple>
        id={id}
        highlightItemOnHover={isDrawer ? false : highlightItemOnHover}
        items={derivedItems}
        modal={isDrawer ? false : modal}
        multiple={multiple}
        name={name}
        open={open}
        onOpenChange={setOpen}
        value={value}
        onValueChange={(nextValue, eventDetails) => {
          if (!isValueControlled) {
            setInternalValue(
              nextValue as SelectValueType<Value, Multiple> | null,
            );
          }

          if (nextValue !== null) {
            onValueChange?.(
              nextValue as SelectValueType<Value, Multiple>,
              eventDetails,
            );
          }
        }}
        {...props}
      >
        {children}
      </SelectPrimitive.Root>
    </SelectSurfaceContext.Provider>
  );
};

const SelectGroup = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) => (
  <SelectPrimitive.Group
    ref={ref}
    className={(state) => cn(resolveClassName(className, state))}
    {...props}
  />
);

const SelectValue = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) => (
  <SelectPrimitive.Value
    ref={ref}
    className={(state) =>
      cn("flex min-w-0 items-center", resolveClassName(className, state))
    }
    {...props}
  />
);

const SelectTrigger = ({
  ref,
  className,
  children,
  nativeButton = false,
  showIcon = true,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  showIcon?: boolean;
}) => (
  <SelectPrimitive.Trigger
    ref={ref}
    nativeButton={nativeButton}
    type="button"
    className={(state) =>
      cn(
        `
          flex
          h-9
          w-full
          items-center
          justify-between rounded-full border border-border-light bg-input px-3 py-2
          text-sm whitespace-nowrap text-muted-foreground ring-offset-background placeholder:text-muted-foreground
          hover:border-foreground hover:text-primary
          focus:border-foreground
          focus:ring-0 focus:ring-transparent focus:outline-hidden
          disabled:cursor-not-allowed disabled:opacity-50
          [&>span]:min-w-0
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  >
    {children}
    {showIcon && (
      <SelectPrimitive.Icon
        render={
          <CaretSortIcon className="size-4 shrink-0 text-foreground/75" />
        }
      />
    )}
  </SelectPrimitive.Trigger>
);
SelectTrigger.displayName = "SelectTrigger";

const SelectScrollUpButton = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) => (
  <SelectPrimitive.ScrollUpArrow
    ref={ref}
    className={(state) =>
      cn(
        "flex cursor-default items-center justify-center py-1",
        resolveClassName(className, state),
      )
    }
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpArrow>
);
SelectScrollUpButton.displayName = "SelectScrollUpButton";

const SelectScrollDownButton = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) => (
  <SelectPrimitive.ScrollDownArrow
    ref={ref}
    className={(state) =>
      cn(
        "flex cursor-default items-center justify-center py-1",
        resolveClassName(className, state),
      )
    }
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownArrow>
);
SelectScrollDownButton.displayName = "SelectScrollDownButton";

const SelectContent = ({
  className,
  children,
  align = "center",
  alignItemWithTrigger,
  alignOffset = 0,
  drawerBodyClassName,
  drawerClassName,
  drawerDescription,
  drawerListClassName,
  drawerTitle,
  side = "bottom",
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Popup> &
  Pick<
    React.ComponentProps<typeof SelectPrimitive.Positioner>,
    "align" | "alignItemWithTrigger" | "alignOffset" | "side" | "sideOffset"
  > & {
    drawerBodyClassName?: string;
    drawerClassName?: string;
    drawerDescription?: string;
    drawerListClassName?: string;
    drawerTitle?: string;
  }) => {
  const { isDrawer, open, sectionRadius, setOpen, title } =
    useSelectSurfaceContext();
  const shouldAlignItemWithTrigger = alignItemWithTrigger ?? false;

  if (isDrawer) {
    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Positioner
          align={align}
          alignItemWithTrigger={false}
          alignOffset={alignOffset}
          side={side}
          sideOffset={sideOffset}
        >
          <Drawer
            nested
            open={open}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) {
                setOpen(false);
              }
            }}
          >
            <DrawerContent
              className={cn(
                "border-border-light bg-background p-0",
                drawerClassName,
              )}
              bodyClassName={cn(
                "flex flex-col overflow-hidden",
                drawerBodyClassName,
              )}
            >
              <DrawerTitle className="sr-only">
                {drawerTitle ?? title}
              </DrawerTitle>
              <DrawerDescription className="sr-only">
                {drawerDescription ?? drawerTitle ?? title}
              </DrawerDescription>
              <SelectPrimitive.Popup
                className={(state) =>
                  cn(
                    `
                      pointer-events-auto relative z-100 flex min-h-0 w-full
                      flex-col overflow-hidden bg-transparent
                      text-muted-foreground outline-hidden
                    `,
                    resolveClassName(className, state),
                  )
                }
                style={
                  {
                    "--select-drawer-section-radius": sectionRadius,
                  } as React.CSSProperties
                }
                {...props}
              >
                <SelectPrimitive.List
                  className={cn(
                    `
                      max-h-[min(70vh,560px)] min-h-0 overflow-y-auto
                      overscroll-contain p-3 [-webkit-overflow-scrolling:touch]
                    `,
                    drawerListClassName,
                  )}
                >
                  {children}
                </SelectPrimitive.List>
              </SelectPrimitive.Popup>
            </DrawerContent>
          </Drawer>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    );
  }

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        align={align}
        alignItemWithTrigger={shouldAlignItemWithTrigger}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="pointer-events-auto isolate z-100"
      >
        <SelectPrimitive.Popup
          className={(state) =>
            cn(
              `
                pointer-events-auto relative z-100
                max-h-96 min-w-32 origin-(--transform-origin) overflow-hidden rounded-md border bg-popover
                text-muted-foreground shadow-md outline-hidden
                transition duration-150 ease-out
                data-ending-style:scale-95 data-ending-style:opacity-0
                data-starting-style:scale-95 data-starting-style:opacity-0
              `,
              !shouldAlignItemWithTrigger && "min-w-(--anchor-width)",
              resolveClassName(className, state),
            )
          }
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List
            className={cn(
              `
                max-h-[min(var(--available-height),24rem)] overflow-y-auto p-1
              `,
              !shouldAlignItemWithTrigger &&
                `
                  w-full
                  min-w-(--anchor-width)
                `,
            )}
          >
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
};
SelectContent.displayName = "SelectContent";

const SelectLabel = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.GroupLabel>) => (
  <SelectPrimitive.GroupLabel
    ref={ref}
    className={(state) =>
      cn(
        "px-2 py-1.5 text-sm font-semibold",
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
SelectLabel.displayName = "SelectLabel";

const SelectItem = ({
  ref,
  className,
  children,
  disabled,
  onClick,
  value,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => {
  const surface = useOptionalSelectSurfaceContext();
  const isDrawer = surface?.isDrawer ?? false;
  const itemValue = value ?? null;

  const handleClick = React.useCallback(
    (event: PreventableSelectItemClickEvent) => {
      onClick?.(event);

      if (
        event.defaultPrevented ||
        !isDrawer ||
        !surface ||
        disabled ||
        event.button !== 0
      ) {
        return;
      }

      event.preventBaseUIHandler();
      surface.commitDrawerItemValue(itemValue, event.nativeEvent);
    },
    [disabled, isDrawer, itemValue, onClick, surface],
  );

  return (
    <SelectPrimitive.Item
      ref={ref}
      className={(state) =>
        cn(
          isDrawer
            ? cn("group", selectDrawerItemClassName)
            : `
                relative
                flex w-full
                cursor-pointer items-center rounded-sm py-1.5 pr-8 pl-2 text-sm text-muted
                outline-hidden select-none focus:bg-accent focus:text-primary
                data-disabled:pointer-events-none data-disabled:opacity-50
              `,
          resolveClassName(className, state),
        )
      }
      data-drawer-select-item={isDrawer ? "" : undefined}
      disabled={disabled}
      onClick={handleClick}
      value={itemValue}
      {...props}
    >
      <span
        className={cn(
          "absolute flex size-3.5 items-center justify-center",
          isDrawer ? "right-4" : "right-2",
        )}
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText
        className={cn(
          isDrawer &&
            `
              min-w-0 flex-1 leading-5 wrap-break-word whitespace-normal
            `,
        )}
      >
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
};
SelectItem.displayName = "SelectItem";

const selectDrawerItemClassName = `
  unforce-radius relative flex min-h-12 w-full cursor-pointer items-center
  rounded-none border-b border-border-light bg-card p-4 pr-12 text-left text-sm
  text-foreground outline-hidden select-none
  first:rounded-t-[var(--select-drawer-section-radius)]
  last:rounded-b-[var(--select-drawer-section-radius)] last:border-b-0
  [[role=separator]+&]:rounded-t-[var(--select-drawer-section-radius)]
  [&:has(+_[role=separator])]:rounded-b-[var(--select-drawer-section-radius)]
  [&:has(+_[role=separator])]:border-b-0 hover:bg-accent hover:text-muted-foreground
  focus:bg-accent focus:text-muted-foreground data-disabled:pointer-events-none
  data-disabled:opacity-50
`;

const SelectSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) => {
  const surface = useOptionalSelectSurfaceContext();
  const isDrawer = surface?.isDrawer ?? false;

  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={(state) =>
        cn(
          isDrawer ? "h-3" : "-mx-1 my-1 h-px bg-muted",
          resolveClassName(className, state),
        )
      }
      {...props}
    />
  );
};
SelectSeparator.displayName = "SelectSeparator";

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
